import { Link } from 'react-router-dom';
import { profile } from '@/mocks/profile';
import { works } from '@/mocks/works';
import Breadcrumb from '@/components/feature/Breadcrumb';
import useSeo from '@/hooks/useSeo';

export default function Profile() {
  useSeo({
    title: '吉泽明步个人资料与代表作品',
    description:
      '吉泽明步个人资料与代表作品一览：职业为影视演员，活跃于电影、电视剧与短片领域，代表作包括《遥远的星空》《时间的皱褶》《沉默的证明》等。',
    keywords: '吉泽明步,个人资料,明星资料,代表作品,演员资料,影视演员',
  });

  const representative = works.filter((w) => profile.representativeIds.includes(w.id));

  return (
    <div className="w-full">
      <div className="border-b border-background-200/70 bg-background-100/60">
        <div className="container-page py-6 md:py-8">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '明星资料' }]} />
        </div>
      </div>

      <div className="container-page py-8 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[340px_1fr] lg:gap-12">
          <div>
            <div className="mx-auto w-full max-w-[340px] overflow-hidden rounded-lg border border-background-200/70 lg:mx-0" style={{ aspectRatio: '4 / 5' }}>
              <img
                src={profile.avatar}
                alt={`${profile.name} 个人形象照`}
                title={`${profile.name} - 影视演员个人资料`}
                className="h-full w-full object-top"
              />
            </div>
            <div className="mx-auto mt-5 grid max-w-[340px] grid-cols-2 gap-3 lg:mx-0">
              {profile.stats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200/70 bg-background-100/60 p-4 text-center">
                  <p className="font-heading text-xl font-semibold text-primary-700">
                    {stat.value}
                    <span className="ml-0.5 text-xs font-normal text-foreground-500">{stat.unit}</span>
                  </p>
                  <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="text-xs font-medium uppercase tracking-[0.28em] text-accent-600">Profile</span>
            <h1 className="mt-3 font-heading text-3xl font-semibold text-foreground-950 md:text-4xl">
              {profile.name}
            </h1>
            <p className="mt-1 text-sm uppercase tracking-[0.2em] text-foreground-500">{profile.nameEn}</p>
            <p className="mt-4 inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800">
              {profile.tagline}
            </p>

            <div className="mt-6 space-y-4 text-base leading-loose text-foreground-700">
              <p>{profile.intro}</p>
              <p>{profile.introExtra}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {profile.traits.map((trait) => (
                <span key={trait} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                  {trait}
                </span>
              ))}
            </div>
          </div>
        </div>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">基本信息</h2>
          <div className="mt-5 grid grid-cols-1 overflow-hidden rounded-lg border border-background-200/70 sm:grid-cols-2">
            {profile.facts.map((fact, index) => (
              <div
                key={fact.label}
                className={[
                  'flex items-center justify-between gap-4 px-5 py-4',
                  index % 2 === 0 ? 'bg-background-50' : 'bg-background-100/60',
                ].join(' ')}
              >
                <span className="text-sm text-foreground-500">{fact.label}</span>
                <span className="text-right text-sm font-medium text-foreground-900">{fact.value}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 md:mt-16">
          <div className="mb-6 flex items-end justify-between">
            <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">代表作品一览</h2>
            <Link to="/works" className="inline-flex items-center gap-1 text-sm text-primary-700 hover:text-primary-800 cursor-pointer whitespace-nowrap">
              全部作品
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {representative.map((work) => (
              <Link
                key={work.id}
                to={`/works/${work.id}`}
                className="group flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-300 cursor-pointer"
              >
                <div className="h-24 w-16 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={work.cover}
                    alt={`${work.title} 作品封面`}
                    title={`${work.title} - 吉泽明步作品`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {work.title}
                  </h3>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-foreground-500">
                    <span>{work.year}</span>
                    <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                    <span>{work.type}</span>
                    <span className="h-1 w-1 rounded-full bg-foreground-300"></span>
                    <span>{work.roleType}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">{work.summary}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-5">
            <Link
              to="/news"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-800 whitespace-nowrap transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
            >
              <i className="ri-newspaper-line" aria-hidden="true"></i>
              查看影视动态
            </Link>
          </div>
        </section>

        <section className="mt-12 md:mt-16">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">演艺历程</h2>
          <ol className="mt-6 space-y-0">
            {profile.timeline.map((item, index) => (
              <li key={item.year} className="relative flex gap-5 pb-8 last:pb-0">
                <div className="flex flex-col items-center">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-background-50">
                    {item.year.slice(2)}
                  </span>
                  {index !== profile.timeline.length - 1 && (
                    <span className="mt-1 w-px flex-1 bg-background-300"></span>
                  )}
                </div>
                <div className="pt-1.5">
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{item.title}</h3>
                    <span className="text-xs text-accent-600">{item.year}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
}